import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import moment from "moment";
import { Button, message, Spin } from 'antd';
import { SendOutlined, AudioOutlined, BorderOutlined } from "@ant-design/icons";

// 元件引入
import Message from "./components/Message";
import MessageLoading from "./components/MessageLoading";
import AiCorner from "./components/AiCorner";
import MicrophoneIcon from "./components/MicrophoneIcon";
import AudioVisualization from "./components/AudioVisualization";

// Hooks 引入
import useMessageStore from "./hooks/useMessageStore";
import { useUserStore } from "./hooks/useUserStore";
import { useDemand } from "../../context/DemandContext";
import { useDemandDataLoader } from "../../hooks/demand/useDemandDataLoader";
import { useFilesByIds, useFileManagementContext } from "../../context/FileManagementContext";

// 常數引入
import { API_CONFIG, TIME_RANGES } from "./constants";

// 新增引入
import { useVoiceChat } from "./modules/voiceChat/hooks/useVoiceChat";
import { createTeamsyncApi } from "../../services/teamsyncApi";

const posterSrc = "/images/output.png";

// 滾動到底部 Hook
const useScrollToBottom = () => {
  const isUserScrolling = useRef(false);
  const scrollTimeout = useRef(null);
  const isAtBottom = useRef(false);
  const lastScrollTime = useRef(0);

  const scrollToBottom = useCallback((immediate = false) => {
    // 檢查滾動節流 - 防止過於頻繁的滾動，但不影響消息追蹤
    const now = Date.now();
    if (!immediate && now - lastScrollTime.current < 50) return;
    lastScrollTime.current = now;

    // 如果使用者正在滾動，且不是強制滾動，則不執行自動滾動
    if (isUserScrolling.current && !immediate) return;

    requestAnimationFrame(() => {
      const chatBox = document.querySelector(".chat_box");
      const bottomElement = document.querySelector(".bottom_element");

      if (!chatBox || !bottomElement) return;

      // 檢查是否已經在底部，避免不必要的滾動
      const currentPosition =
        chatBox.scrollHeight - chatBox.scrollTop - chatBox.clientHeight;
      isAtBottom.current = currentPosition < 5;

      // 如果已經在底部且不是強制滾動，則不需要再次滾動
      if (isAtBottom.current && !immediate) return;

      if (immediate) {
        bottomElement.scrollIntoView({ block: "end" });
        return;
      }

      const isNearBottom = currentPosition < 100; // 增加近底部的判定範圍

      if (isNearBottom) {
        bottomElement.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }
    });
  }, []);

  // 自動滾動效果
  const startAutoScroll = useCallback(() => {
    scrollToBottom(true);

    const interval = setInterval(() => {
      if (!isUserScrolling.current) {
        scrollToBottom(false);
      }
    }, 100); // 調回更短的間隔來跟上打字速度

    return () => clearInterval(interval);
  }, [scrollToBottom]);

  // 處理使用者滾動的函數
  const handleUserScroll = useCallback(() => {
    isUserScrolling.current = true;

    // 清除之前的 timeout
    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }

    // 設置新的 timeout，在使用者停止滾動後恢復自動滾動
    scrollTimeout.current = setTimeout(() => {
      isUserScrolling.current = false;

      // 檢查是否在底部
      const chatBox = document.querySelector(".chat_box");
      if (chatBox) {
        isAtBottom.current =
          chatBox.scrollHeight - chatBox.scrollTop - chatBox.clientHeight < 5;
      }
    }, 200); // 縮短等待時間以更快恢復自動滾動
  }, []);

  return { scrollToBottom, startAutoScroll, handleUserScroll };
};

// 主元件
const Interactive = () => {
  const navigate = useNavigate();
  const userStore = useUserStore();
  const { profile: userProfile } = userStore;

  // 需求單數據相關
  const { state } = useDemand();
  const { isLoading: isItemLoading, error, loadCurrentMemberData } = useDemandDataLoader();
  const hasLoadedRef = useRef(false); // 追蹤是否已經載入過

  const {
    messages,
    addMessage,
    updateMessage,
    deleteMessage,
    stopAudio,
    isPlaying,
  } = useMessageStore({
    initialMessages: [
      {
        text: `${userProfile?.nickname || ""} 您好，有什麼能幫助您的地方？`,
        sender: "ai",
        audio: null,
        id: "default message",
        createAt: new Date(),
      },
    ],
  });

  // 狀態管理
  const [aiCorner, setAiCorner] = useState("aiStart");
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [hasTalked, setHasTalked] = useState(false);
  const userTmpIdRef = useRef("");
  const aiMessageIdRef = useRef("");
  // const [isVideoLoaded, setIsVideoLoaded] = useState(false); // 不再需要
  const [inputText, setInputText] = useState("");
  const [isTextChatResponding, setIsTextChatResponding] = useState(false); // 追蹤文字聊天回覆狀態

  // 從需求單數據中獲取 AI 角色視頻 - 每個動作都各自選用 selected
  const selectedVideoIds = state.ceremony?.liveStream?.files?.selected?.createdVideo || [];
  const createdVideoIds = state.ceremony?.liveStream?.files?.created?.video || [];
  
  // 獲取所有需要的影片 ID（selected 和 created 合併，用於檔案載入）
  const allVideoIds = [
    ...selectedVideoIds.map(v => v.fileId),
    ...createdVideoIds.map(v => v.fileId)
  ];
  const allVideoFiles = useFilesByIds(allVideoIds);
  
  // 建立 fileId 到檔案的映射
  const fileIdToFileMap = {};
  allVideoFiles.forEach(file => {
    if (file && file.id) {
      fileIdToFileMap[file.id] = file;
    }
  });

  // 創建分類的視頻 URL 物件 - 每個動作都各自選用 selected
  const { aiVideoData, videoDebugInfo } = useMemo(() => {
    if (allVideoFiles.length === 0) {
      return {
        aiVideoData: {
          idle: {
            smile: undefined,
            wave: undefined
          },
          talking: undefined
        },
        videoDebugInfo: null
      };
    }

    // 根據 actionType 分類影片 - 每個動作都各自選用 selected
    const videosByActionType = {};

    // 優先處理選中的影片
    selectedVideoIds.forEach(selectedVideo => {
      const file = fileIdToFileMap[selectedVideo.fileId];
      if (file && file.url && selectedVideo.actionType) {
        videosByActionType[selectedVideo.actionType] = file.url;
        console.log(`[Interactive] 使用選中的影片 ${selectedVideo.actionType}:`, file.url);
      }
    });

    // 如果某個 actionType 沒有選中的影片，則從創建的影片中尋找備用
    const requiredActionTypes = ['smile', 'talk', 'wave_hand'];
    requiredActionTypes.forEach(actionType => {
      if (!videosByActionType[actionType]) {
        // 尋找創建的影片中對應的 actionType
        const createdVideo = createdVideoIds.find(video => video.actionType === actionType);
        if (createdVideo) {
          const file = fileIdToFileMap[createdVideo.fileId];
          if (file && file.url) {
            videosByActionType[actionType] = file.url;
            console.log(`[Interactive] 使用創建的影片作為 ${actionType} 備用:`, file.url);
          }
        }
      }
    });

    console.log('[Interactive] 最終根據 actionType 分類的影片:', videosByActionType);
    console.log('[Interactive] talking 影片 URL:', videosByActionType.talk);
    console.log('[Interactive] 是否有 talk actionType 的選中影片:', selectedVideoIds.some(v => v.actionType === 'talk'));

    // 建立最終的影片物件，使用 actionType 映射
    const aiVideoData = {
      idle: {
        smile: videosByActionType.smile,
        wave: videosByActionType.wave_hand || videosByActionType.smile
      },
      talking: videosByActionType.talk || videosByActionType.smile
    };

    // 建立調試信息
    const videoDebugInfo = {
      videosByActionType,
      selectedVideoIds,
      createdVideoIds,
      fileIdToFileMap
    };

    return { aiVideoData, videoDebugInfo };
  }, [selectedVideoIds, createdVideoIds, allVideoFiles, fileIdToFileMap]);

  // 從需求單數據中獲取背景圖片
  const backgroundFileIds = state.ceremony?.liveStream?.files?.my?.background?.map(file => file.fileId) || [];
  const backgroundFiles = useFilesByIds(backgroundFileIds);
  const backgroundUrl = backgroundFiles.length > 0 && backgroundFiles[0]?.url ? backgroundFiles[0].url : null;

  // 檢查 FileManagementContext 狀態
  const { state: fileState } = useFileManagementContext();

  // 調試信息
  console.log('[Interactive] 視頻載入調試:', {
    selectedVideoIds: selectedVideoIds.length,
    selectedVideoIdsData: selectedVideoIds,
    createdVideoIds: createdVideoIds.length,
    createdVideoIdsData: createdVideoIds,
    allVideoIds: allVideoIds.length,
    allVideoIdsData: allVideoIds,
    allVideoFiles: allVideoFiles.length,
    allVideoFilesData: allVideoFiles,
    fileIdToFileMap: Object.keys(fileIdToFileMap).length,
    aiVideoData,
    videoDebugInfo,
    fileManagementState: fileState.length,
    allFiles: fileState
  });

  // 使用 useScrollToBottom hook
  const { scrollToBottom, startAutoScroll, handleUserScroll } =
    useScrollToBottom();

  // 數據載入邏輯 - 修正依賴陣列並移除任意延遲
  const loadData = useCallback(async () => {
    // 如果已經載入過，就不再載入
    if (hasLoadedRef.current) {
      return;
    }

    // 從 localStorage 獲取會員資料
    const memberInfo = JSON.parse(localStorage.getItem('memberInfo') || '{}');

    if (memberInfo.projectName) {
      hasLoadedRef.current = true; // 標記為已載入
      console.log('[Interactive] 開始載入需求單數據...');
      try {
        const success = await loadCurrentMemberData();
        if (!success) {
          console.error('[Interactive] 載入需求單數據失敗');
          hasLoadedRef.current = false; // 載入失敗時重置標記
        } else {
          console.log('[Interactive] 需求單數據載入成功');
          // 移除任意延遲 - 檔案會自動載入
        }
      } catch (error) {
        console.error('[Interactive] 載入需求單數據發生異常:', error);
        hasLoadedRef.current = false; // 發生異常時重置標記
      }
    } else {
      message.error('未找到會員資訊');
      navigate('/');
    }
  }, [loadCurrentMemberData, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 處理 AI 回應
  const handleAiResponse = useCallback(
    (data) => {
      if (!data) return;

      // 處理使用者問題顯示
      if (data.question) {
        updateMessage(userTmpIdRef.current, {
          text: data.question?.message,
          sender: "user",
          id: userTmpIdRef.current,
          createAt: moment().toISOString(),
          isLoading: false,
        });
        scrollToBottom(true); // 保持強制滾動確保跟上打字速度
      }

      // 處理 AI 回應
      if (data.message) {
        const audioBlob = data.blobs?.find((blob) =>
          blob.content_type.startsWith("audio/"),
        );

        // 更新已存在的 AI 載入訊息，而不是新增
        if (aiMessageIdRef.current) {
          updateMessage(aiMessageIdRef.current, {
            text: data.message,
            audio: audioBlob?.url,
            sender: "ai",
            id: aiMessageIdRef.current,
            createAt: moment().toISOString(),
            isLoading: false,
          });
        } else {
          // 如果沒有預先建立的 AI 訊息 ID，則新增一個
          const tmpIdAi = uuidv4();
          addMessage({
            text: data.message,
            audio: audioBlob?.url,
            sender: "ai",
            id: tmpIdAi,
            createAt: moment().toISOString(),
            isLoading: false,
          });
        }

        // 如果是文字聊天回覆（沒有音頻），手動控制 AI 角色動畫
        if (!audioBlob) {
          setIsTextChatResponding(true);
          setHasTalked(true);
          setAiCorner("aiTalking");

          // 根據文字長度計算"說話"時間（最少3秒，最多8秒）
          const messageLength = data.message.length;
          const talkingDuration = Math.min(Math.max(messageLength * 100, 3000), 8000);

          setTimeout(() => {
            setIsTextChatResponding(false);
            setAiCorner("aiPending");
          }, talkingDuration);
        }

        scrollToBottom(true); // 保持強制滾動確保跟上打字速度
      }
    },
    [addMessage, updateMessage, scrollToBottom],
  );

  // 處理錯誤
  const handleError = useCallback(
    (error) => {
      console.error("請求失敗", error);

      // 如果有預先建立的 AI 訊息，則更新它
      if (aiMessageIdRef.current) {
        updateMessage(aiMessageIdRef.current, {
          text: "系統忙碌中，請稍後再試",
          sender: "ai",
          id: aiMessageIdRef.current,
          createAt: moment().toISOString(),
          isLoading: false,
        });
      } else {
        // 否則新增一個錯誤訊息
        const tmpIdAi = uuidv4();
        addMessage({
          text: "系統忙碌中，請稍後再試",
          sender: "ai",
          id: tmpIdAi,
          createAt: moment().toISOString(),
          isLoading: false,
        });
      }

      scrollToBottom(true);
    },
    [addMessage, updateMessage, scrollToBottom],
  );

  const voiceChatConfig = useMemo(
    () => ({
      wsUrl: "wss://api.scfg.io/ws/transcribe/yating",
      apiConfig: {
        SERVER_URL: API_CONFIG.SERVER_URL,
        ASR_SERVICE: API_CONFIG.ASR_SERVICE,
        // 動態使用需求單中的聊天室配置，如果沒有則使用預設值
        CHATROOM_ID: state.live?.liveStreamChatroomId || API_CONFIG.CHATROOM_ID,
        API_KEY: state.live?.liveStreamApiKey || API_CONFIG.API_KEY,
        textPayload: {
          additional_args: `{"base_url": "${process.env.REACT_APP_SERVER_URL}"}`,
        },
        textParams: {
          agent_type: "beautician_booking",
          client_id: userProfile?.id,
        },
        voicePayload: {
          additional_args: `{"base_url": "${process.env.REACT_APP_SERVER_URL}"}`,
          agent_type: "beautician_booking",
          client_id: userProfile?.id,
          speech_keywords: ["Panco", "汎歌"],
        },
      },
      disableSpeechRecognition: true,
    }),
    [userProfile?.id, state.live?.liveStreamChatroomId, state.live?.liveStreamApiKey],
  );

  // Teamsync API 配置 - 動態創建實例
  const teamsyncApiInstance = useMemo(() => {
    const apiKey = state.live?.liveStreamApiKey;
    if (!apiKey) return null;

    return createTeamsyncApi({
      mode: 'token',
      credentials: {
        staticToken: apiKey
      },
      serverUrl: 'https://api.scfg.io'
    });
  }, [state.live?.liveStreamApiKey]);

  // Teamsync 發送狀態
  const [isTeamsyncPending, setIsTeamsyncPending] = useState(false);

  // 使用 useVoiceChat hook
  const {
    isRecording,
    isProcessing,
    transcript,
    startRecording: startVoiceRecording,
    stopRecording: stopVoiceRecording,
  } = useVoiceChat({
    onMessageReceived: handleAiResponse,
    onError: handleError,
    config: voiceChatConfig,
  });

  //   // 清除載入中的訊息
  const clearMessages = () => {
    const isLoadingMessages = messages.filter(
      (message) =>
        message.isLoading || message.text === "系統忙碌中，請稍後再試",
    );
    isLoadingMessages.forEach((message) => {
      deleteMessage(message.id);
    });
    stopAudio();
  };

  // 開始錄音
  const handleStartRecording = () => {
    clearMessages();
    if (!isRecording && !isProcessing) {
      stopAudio();

      const tmpId = uuidv4();
      userTmpIdRef.current = tmpId;

      const aiTmpId = uuidv4();
      aiMessageIdRef.current = aiTmpId;

      addMessage({
        text: "錄音開始",
        sender: "user",
        id: tmpId,
        createAt: moment().toISOString(),
        isLoading: true,
      });

      scrollToBottom(true);
      startVoiceRecording();
    }
  };

  // 停止錄音
  const handleStopRecording = () => {
    if (isRecording) {
      stopVoiceRecording();

      setTimeout(() => {
        addMessage({
          text: "ai 正在思考",
          sender: "ai",
          id: aiMessageIdRef.current,
          createAt: moment().toISOString(),
          isLoading: true,
        });

        scrollToBottom(true);
      }, 1000);
    }
  };

  // 更新使用者訊息文字 (語音辨識)
  useEffect(() => {
    if (!transcript || !userTmpIdRef.current) return;

    updateMessage(userTmpIdRef.current, {
      text: transcript,
      sender: "user",
      id: userTmpIdRef.current,
      createAt: moment().toISOString(),
      isLoading: false,
    });

    scrollToBottom(true);
  }, [transcript]);

  // 處理 AI 動畫狀態
  useEffect(() => {
    let cleanup;

    if (isPlaying) {
      cleanup = startAutoScroll();
      setHasTalked(true);
      setAiCorner("aiTalking");
    } else if (!isFirstLoad && !isTextChatResponding) {
      // 只有在不是文字聊天回覆狀態時才切換到 pending
      setAiCorner("aiPending");
      stopAudio();
    }

    return () => {
      if (cleanup) cleanup();
    };
  }, [isPlaying, isFirstLoad, isTextChatResponding, startAutoScroll]);

  // 初始化
  useEffect(() => {
    if (isFirstLoad) setAiCorner("aiStart");
    const timer = setTimeout(() => {
      if (!hasTalked) setAiCorner("aiPending");
      setIsFirstLoad(false);
    }, 5000);

    return () => {
      clearTimeout(timer);
      aiMessageIdRef.current = "";
      userTmpIdRef.current = "";
      clearMessages();
      setHasTalked(false);
      setAiCorner("aiStart");
      setIsFirstLoad(true);
    };
  }, []);

  // 處理消息更新時的滾動
  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom(true);
    }
  }, [messages, scrollToBottom]);

  // 處理文字輸入發送
  const handleSendMessage = useCallback(() => {
    if (!inputText.trim() || isProcessing || isTeamsyncPending) return;

    const chatroomId = state.live?.liveStreamChatroomId;
    const apiKey = state.live?.liveStreamApiKey;

    // 檢查是否有必要的配置
    if (!chatroomId || !apiKey) {
      console.warn('[Interactive] 缺少 Teamsync 配置:', { chatroomId, apiKey: !!apiKey });
      message.error('聊天室配置不完整，請檢查設定');
      return;
    }

    clearMessages();
    stopAudio();

    const tmpId = uuidv4();
    userTmpIdRef.current = tmpId;

    const aiTmpId = uuidv4();
    aiMessageIdRef.current = aiTmpId;

    const messageText = inputText.trim();

    // 添加用戶消息
    addMessage({
      text: messageText,
      sender: "user",
      id: tmpId,
      createAt: moment().toISOString(),
      isLoading: false,
    });

    // 清空輸入框
    setInputText("");

    // 添加 AI 思考消息
    setTimeout(() => {
      addMessage({
        text: "ai 正在思考",
        sender: "ai",
        id: aiMessageIdRef.current,
        createAt: moment().toISOString(),
        isLoading: true,
      });
      scrollToBottom(true);
    }, 500);

    // 發送 Teamsync 消息
    if (teamsyncApiInstance) {
      setIsTeamsyncPending(true);

      teamsyncApiInstance.chatWithAi({
        chatroomId: chatroomId,
        agentType: 'image_reader',
        channelType: 'chatroom',
        tts: false,
        chatType: 'ai_query',
        message: messageText,
        blobIds: [],
        additionalArgs: '{"key": "value"}'
      })
        .then((data) => {
          console.log('[Interactive] Teamsync AI 回應成功:', data);
          handleAiResponse({
            message: data.message || data.content || '收到回應',
            question: { message: messageText }
          });
        })
        .catch((error) => {
          console.error('[Interactive] Teamsync AI 回應失敗:', error);
          handleError(error);
        })
        .finally(() => {
          setIsTeamsyncPending(false);
        });
    } else {
      console.error('[Interactive] Teamsync API 實例未初始化');
      message.error('聊天服務未初始化');
    }

    scrollToBottom(true);
  }, [
    inputText,
    isProcessing,
    isTeamsyncPending,
    state.live?.liveStreamChatroomId,
    state.live?.liveStreamApiKey,
    teamsyncApiInstance,
    clearMessages,
    stopAudio,
    addMessage,
    scrollToBottom,
    handleAiResponse,
    handleError
  ]);

  // 處理 Enter 鍵發送
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  return (
    <Spin spinning={isItemLoading} tip="載入需求單數據中...">
      <div className="mx-auto flex h-[calc(100vh-64px)] md:h-[100vh] w-full flex-1 flex-col bg-white shadow">
        <div className="flex-1 bg-white">
          <div className="relative flex h-full w-full flex-col gap-5 bg-cover bg-center bg-no-repeat">
            {/* 漸層背景 */}
            <div
              className="absolute bottom-0 left-0 z-[1] h-1/4 w-full"
              style={{
                background:
                  "linear-gradient(180deg, rgba(253, 234, 224, 0) 0%, rgba(253, 234, 224, 0.680874) 33.38%, #FDEAE0 65.86%)",
              }}
            />

            {/* AI 角色動畫 */}
            <AiCorner
              activeVideo={aiCorner}
              videoData={aiVideoData}
              backgroundUrl={backgroundUrl}
              posterSrc={backgroundUrl}
              videoDebugInfo={videoDebugInfo}
            // onVideoLoaded={setIsVideoLoaded} // 不再需要
            />

            <div
              className="absolute bottom-0 left-0 z-[0] h-full w-full opacity-0"
              style={{
                backgroundImage: `url(${posterSrc})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
            ></div>

            {/* 聊天區域 - 分離消息列表和輸入框 */}
            {/* 消息列表 */}
            <div className="z-[2] opacity-100">
              <div className="absolute bottom-20 z-[3] flex h-[45%] w-full flex-col overflow-x-hidden">
                <div
                  className="chat_box relative flex-1 flex flex-col gap-3 overflow-y-auto scroll-smooth px-5 py-2"
                  onScroll={handleUserScroll}
                >
                  {messages.map((message, index) => (
                    <React.Fragment key={index}>
                      {message.isLoading ? (
                        <MessageLoading message={message} />
                      ) : (
                        <Message message={message} />
                      )}
                    </React.Fragment>
                  ))}
                  <div className="bottom_element invisible" />
                </div>
              </div>
            </div>

            {/* 底部輸入區域 - 根據 Figma 設計 */}
            <div className="absolute bottom-0 z-[4] w-full">
              <div className="bg-[#FDFDFB] shadow-[0px_-2px_8px_0px_rgba(0,0,0,0.05)] p-4">
                <div className="flex flex-col items-stretch gap-3">
                  {/* 輸入框區域 */}
                  <div className="flex-1 flex items-stretch gap-2">
                    <div className="flex-1 bg-[#F4F5F7] border border-[#EEEEEE] rounded-lg pl-2 flex items-center justify-between gap-4">
                      <input
                        type="text"
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="你好"
                        className="flex-1 bg-transparent text-[#717D8B] text-sm outline-none placeholder-[#717D8B] font-normal"
                        disabled={isRecording || isProcessing || isTeamsyncPending}
                        style={{
                          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                          fontSize: '14px',
                          lineHeight: '1.3'
                        }}
                      />
                      <Button
                        type="text"
                        shape="circle"
                        onClick={handleSendMessage}
                        disabled={!inputText.trim() || isRecording || isProcessing || isTeamsyncPending}
                        icon={<SendOutlined
                          className={`${!inputText.trim() || isRecording || isProcessing || isTeamsyncPending ? 'text-[#717D8B]' : 'text-[#D7862A]'}`}
                        />}
                      />

                    </div>
                    {/* 麥克風按鈕 */}
                    <Button
                      type="text"
                      shape="circle"
                      onClick={isRecording ? handleStopRecording : handleStartRecording}
                      disabled={isProcessing}
                      className={`${isRecording ? 'text-[#D7862A]' : 'text-[#717D8B]'}`}
                      icon={isRecording ? <BorderOutlined className="text-[#D7862A]" /> : <AudioOutlined className="text-[#717D8B]" />}
                    />
                  </div>
                  {/* <AudioVisualization /> */}
                </div>
              </div>
            </div>

            {/* 音頻播放器 (隱藏) */}
            <audio
              id="audio-player"
              src="https://teamsyncblob.s3.ap-southeast-1.amazonaws.com/ChatRoom_Speech/2cac0736-24dc-42ea-a7c6-7aa533511b44/ad10e402-aeca-4f23-a375-44235cd77b1f.mp3"
              autoPlay
              controls={false}
              controlsList="nodownload"
              className="hidden"
            />

            {/* AI 肌膚檢測按鈕 */}
            {/* {!isRecording && (
            <div
              onClick={() => navigate("/skin-analyze")}
              className="absolute bottom-10 right-[20%] z-[10] flex translate-x-1/2 cursor-pointer flex-col items-center rounded-xl border-[0.7px] border-main bg-[#FFFFFFA6] px-2 py-1.5 shadow-[0px_1px_10px_0px_#C53B3B] xs:bottom-12 sm:bottom-16 lg:bottom-24"
            >
              <img
                src="/icons/ai-skin-pink.svg"
                alt="ai-skin"
                className="h-8 w-8 [&>*]:fill-main"
              />
              <span className="text-[10px] font-semibold leading-[26px] tracking-[0.05em] text-main">
                AI 肌膚檢測
              </span>
            </div>
          )} */}
          </div>
        </div>
      </div>
    </Spin>
  );
};

export default Interactive;
